import { HiddenFieldsAlert } from "@/shared/value";
import Checkbox from "@/shared/form/checkbox";
import Form from "@/shared/form/form";
import Input from "@/shared/form/input";
import { LocalizedValue } from "@/shared/form/localized-value";
import { Attachments } from "@/shared/form/upload";
import { useSaveCompanyDocumentMutation } from "../model/use-save-company-document-mutation";
import { useCompanyDocumentEntity } from "../model/use-company-document-entity";
import { FieldsType } from "../model/types";
import { useTranslations } from "use-intl";
import { ReadConfirmationTypeSelect } from "@/modules/documents/entities/read-confirmation-type-select";
import { CompanyDocumentFolderSelect } from "@/modules/documents/entities/company-document-folder-select";
import { SaveCompanyDocumentSubmit } from "./save-company-document-submit";

export function SaveCompanyDocumentsForm({
  id,
  folderId,
  disableFolder,
  afterSubmit,
}: {
  id?: string;
  folderId?: string;
  disableFolder?: boolean;
  afterSubmit?: () => void;
}) {
  const t = useTranslations("Documents");

  const {
    entity,
    formKey,
    loading: entityLoading,
    initialReadConfirmationType,
    hasConfirmations,
    hasHiddenFields,
  } = useCompanyDocumentEntity({ id, folderId });
  const { save, loading } = useSaveCompanyDocumentMutation({ afterSubmit });

  return (
    <>
      {hasHiddenFields && <HiddenFieldsAlert />}

      <Form
        key={formKey}
        disabled={loading || entityLoading || hasHiddenFields}
        layout="vertical"
        onFinish={save}
        initialValues={entity}
      >
        <Form.Item<FieldsType> name="id" hidden>
          <Input />
        </Form.Item>

        <LocalizedValue
          name="name"
          label={t("Name")}
          render={({ placeholder }) => <Input placeholder={placeholder} />}
          myLocaleRules={[{ required: true }]}
        />

        <LocalizedValue
          name="description"
          label={t("Description")}
          render={({ placeholder }) => (
            <Input.TextArea placeholder={placeholder} />
          )}
          myLocaleRules={[{ required: true }]}
        />

        <Form.Item<FieldsType> name="folderId" label={t("Folder")}>
          <CompanyDocumentFolderSelect disabled={disableFolder} />
        </Form.Item>

        <Form.Item<FieldsType>
          name="readConfirmationType"
          label={t("Read confirmation type")}
          rules={[{ required: true }]}
        >
          <ReadConfirmationTypeSelect />
        </Form.Item>

        <Form.Item<FieldsType>
          name="isActive"
          label={t("Is active")}
          valuePropName="checked"
        >
          <Checkbox />
        </Form.Item>

        <Form.Item<FieldsType>
          name="isAssignToNewPersons"
          label={t("Is assign to new persons")}
          valuePropName="checked"
        >
          <Checkbox />
        </Form.Item>

        <Form.Item<FieldsType>
          name="file"
          label={t("File")}
          rules={[{ required: true }]}
          getValueProps={(value) => ({ value: value ? [value] : undefined })}
          normalize={(value) => (Array.isArray(value) ? value[0] : value)}
        >
          <Attachments maxCount={1} />
        </Form.Item>

        <SaveCompanyDocumentSubmit
          loading={loading}
          initialReadConfirmationType={initialReadConfirmationType}
          hasConfirmations={hasConfirmations}
        />
      </Form>
    </>
  );
}
